import { useMemo } from "react";
import { useAdp } from "../context/AdpContext";
import { useDraftData } from "../context/DraftDataContext";

export interface MarketAdp {
  /**
   * Normalized name → market ADP for the loaded portfolio's *majority* provider.
   * Kept for back-compat and single-provider surfaces. For a mixed UD+DK
   * portfolio, prefer `lookupForProvider(row.Provider)` so each pick is scored
   * against its own platform's market.
   */
  marketAdpLookup: Map<string, number>;
  /** Column/label text for the majority market ("DK ADP" | "UD ADP"). */
  marketLabel: string;
  /** Underdog market map (live or the user's reference-date snapshot). */
  udMarketLookup: Map<string, number>;
  /** DraftKings market map (current DK ADP). */
  dkMarketLookup: Map<string, number>;
  /** Pick the correct market map for a given provider label. */
  lookupForProvider: (provider?: string) => Map<string, number>;
  /** Label for a given provider ("DK ADP" | "UD ADP"). */
  labelForProvider: (provider?: string) => string;
}

/**
 * Provider-aware market ADP selection — the single place CLV surfaces choose
 * which market to compare pick numbers against.
 *
 * Underdog and DraftKings have DIFFERENT ADP markets (and different draft sizes:
 * UD is 18-round/216-pick, DK is 20-round/240-pick). A pick's value must be
 * judged against its OWN platform's board — never mixed. Callers that iterate a
 * mixed portfolio should use `lookupForProvider(row.Provider)` per row/team.
 *
 * - DraftKings → current DK ADP (`dkAdpLookup`).
 * - Everything else (Underdog, legacy CSV) → `effectiveAdpLookup` (live or the
 *   user's reference-date snapshot).
 */
export function useMarketAdp(): MarketAdp {
  const { effectiveAdpLookup, dkAdpLookup } = useAdp();
  const { provider } = useDraftData();
  return useMemo<MarketAdp>(() => {
    const lookupForProvider = (p?: string) =>
      p === "DraftKings" ? dkAdpLookup : effectiveAdpLookup;
    const labelForProvider = (p?: string) =>
      p === "DraftKings" ? "DK ADP" : "UD ADP";
    return {
      marketAdpLookup: lookupForProvider(provider),
      marketLabel: labelForProvider(provider),
      udMarketLookup: effectiveAdpLookup,
      dkMarketLookup: dkAdpLookup,
      lookupForProvider,
      labelForProvider,
    };
  }, [provider, effectiveAdpLookup, dkAdpLookup]);
}
